import { Command, RefreshCw } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Spinner } from './Spinner';

import type { SlashCommandItem } from '../hooks/use-slash-commands';

export interface SlashCommandPickerProps {
  /** Pre-filtered commands to display. Filtering is done by the parent (ChatInput). */
  commands: SlashCommandItem[];
  status: 'idle' | 'loading' | 'success' | 'error';
  selectedIndex: number;
  onSelect: (command: SlashCommandItem) => void;
  onRefresh: () => void;
  onHover?: (index: number) => void;
}

export function SlashCommandPicker({
  commands,
  status,
  selectedIndex,
  onSelect,
  onRefresh,
  onHover,
}: SlashCommandPickerProps) {
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIndex]);

  const showItems = (status === 'success' || status === 'idle') && commands.length > 0;

  return (
    <div
      className="bg-sd-surface-elevated/95 backdrop-blur-md border border-sd-border/60 rounded-sd-lg shadow-sd-lg sd-slide-up overflow-hidden"
      style={{ boxShadow: 'var(--sd-shadow-lg), 0 0 40px rgba(0,0,0,0.04)' }}
      id="slash-command-listbox"
      role="listbox"
      aria-label="Slash Commands"
    >
      {/* Header with subtle gradient */}
      <div className="flex items-center justify-between px-sd-3 py-[6px] border-b border-sd-border/40 bg-gradient-to-r from-sd-surface-elevated to-sd-surface-elevated/80">
        <div className="flex items-center gap-sd-1">
          <Command size={11} className="text-sd-warning opacity-70" />
          <span className="text-sd-xs font-semibold tracking-wide uppercase text-sd-text-muted">Commands</span>
        </div>
        <div className="flex items-center gap-sd-2">
          <span className="text-[10px] text-sd-text-muted/60 select-none hidden sm:inline">
            <kbd className="px-1 py-0.5 bg-sd-surface rounded text-[9px] font-mono border border-sd-border/50">↑↓</kbd>
            {' '}navigate{' '}
            <kbd className="px-1 py-0.5 bg-sd-surface rounded text-[9px] font-mono border border-sd-border/50">↵</kbd>
            {' '}select
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRefresh();
            }}
            className="p-1 rounded-sd-sm text-sd-text-muted hover:text-sd-warning hover:bg-sd-warning/10 transition-all duration-150"
            aria-label="Refresh commands"
          >
            <RefreshCw size={11} />
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="max-h-[260px] overflow-y-auto py-sd-1 px-sd-1">
        {status === 'loading' && (
          <div className="flex items-center gap-sd-2 px-sd-2 py-sd-3">
            <Spinner size="sm" />
            <span className="text-sd-sm text-sd-text-muted">Loading commands...</span>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center justify-between px-sd-2 py-sd-3">
            <span className="text-sd-sm text-sd-text-muted">Failed to load commands</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRefresh();
              }}
              className="text-sd-xs font-medium text-sd-warning hover:text-sd-warning/80 hover:underline transition-colors"
              aria-label="Retry loading commands"
            >
              Retry
            </button>
          </div>
        )}

        {(status === 'success' || status === 'idle') && commands.length === 0 && (
          <div className="px-sd-2 py-sd-3 text-center">
            <span className="text-sd-sm text-sd-text-muted">No matching commands</span>
          </div>
        )}

        {showItems &&
          commands.map((cmd, index) => {
            const isSelected = index === selectedIndex;
            return (
              <div
                key={cmd.name}
                id={`slash-cmd-${cmd.name}`}
                ref={isSelected ? selectedRef : undefined}
                role="option"
                aria-selected={isSelected}
                className={`group relative flex items-start gap-sd-2 pl-sd-3 pr-sd-2 py-[7px] rounded-sd-md cursor-pointer transition-all duration-150 ${
                  isSelected
                    ? 'bg-sd-warning/10 text-sd-text'
                    : 'text-sd-text hover:bg-sd-surface/60'
                }`}
                style={{
                  animation: `sd-reveal-up 200ms cubic-bezier(0.16, 1, 0.3, 1) both ${index * 30}ms`,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(cmd);
                }}
                onMouseEnter={() => onHover?.(index)}
              >
                {/* Amber accent bar */}
                <div
                  className={`absolute left-0 top-[6px] bottom-[6px] w-[2px] rounded-full transition-all duration-150 ${
                    isSelected ? 'bg-sd-warning opacity-100' : 'bg-transparent opacity-0'
                  }`}
                />
                <div className="flex flex-col gap-px min-w-0">
                  <span className="text-sd-sm font-semibold font-mono tracking-tight leading-snug">
                    <span className="text-sd-warning/70">/</span>
                    {cmd.name}
                  </span>
                  <span className="text-sd-xs text-sd-text-secondary leading-snug truncate">{cmd.description}</span>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
